import { localeRegex, pad } from 'tools/string'
import NextRouter from 'next/router'
import { parse, format } from 'url'
import { isBrowser, origin as getOrigin } from 'env'

export const isLocalUrl = (href = '') => {
  const url = parse(href, false, true)
  const origin = parse(getOrigin()(), false, true)

  return !url.host ||
    (url.protocol === origin.protocol && url.host === origin.host)
}

export const createUrl = (_url) => {
  const url = _url || '/'
  return url.startsWith('#') || url.startsWith('/') || url.includes('://') ? url : `http://${url}`
}

export const makeQueryString = (params = {}) => {
  const queryString = Object.keys(params)
    .map((key) => params[key] && `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .filter(x => x)
    .join('&')
  return queryString !== '' ? `?${queryString}` : ''
}

export const getCurrentPage = ({ path }) => {
  const currentPath = Array.isArray(path) ? path : [path]
  const currentPage = currentPath[0] || 'index'
  return currentPage
}

export const parseUrl = (req) => {
  const { url, query } = req
  const path = url.split('#')[0].substring(0, url.indexOf('?') !== -1 ? url.indexOf('?') : url.length).split('/').filter(e => e !== '')
  let locale, previewToken
  // WORKAROUND: String is used for previewMode as next/Link will parse the href object and all queries will be parsed as string.
  let previewMode = 'false'
  if (path[0] && localeRegex.test(path[0])) {
    locale = path[0].split('_').map((part, index) => index === 0 ? part.toLowerCase() : part.toUpperCase()).join('_')
    path.shift()
  }
  if (path[0] === 'preview') {
    previewMode = 'true'
    path.shift()
    previewToken = path[0]
    path.shift()
  }
  return { ...query, path, locale, previewToken, previewMode }
}

export const getUrlObject = (req) => (req || isBrowser())
  ? parse(isBrowser() ? window.location.href : req.originalUrl, true)
  : {}

// Remove and return a query string value from current URL
export const consumeQueryParam = (name, req) => {
  const url = getUrlObject(req)
  const value = url.query[name]
  if (value) {
    // Delete search so that format would use value from query
    delete url.search
    delete url.query[name]
    window.history.replaceState(null, null, format(url))
  }
  return value
}

export const getCurrentUrl = (req, asPath) => req ? `//${req.get('Custom-Url') || req.get('host')}${req.originalUrl}` : `//${window.location.host}${asPath}`

export const dateToRequestString = (input) => {
  if (!input) return input
  const date = input.getDate ? input : new Date(input)
  return `${date.getDate()} ${pad('' + (date.getMonth() + 1))} ${date.getFullYear()}`
}

// WORKAROUND: Each next's Router event can only have one callback (https://github.com/zeit/next.js/issues/2033)
class _Router {
  events = {
    routeChangeStart: 'onRouteChangeStart',
    routeChangeComplete: 'onRouteChangeComplete',
    routeChangeError: 'onRouteChangeError',
    beforeHistoryChange: 'onBeforeHistoryChange',
    hashChangeStart: 'onHashChangeStart',
    hashChangeComplete: 'onHashChangeComplete'
  }

  _subscribers = {}

  _bindListeners = (event) => {
    NextRouter[event] = (...args) => {
      this._subscribers[event].forEach((subscriber) => {
        typeof subscriber === 'function' && subscriber(...args)
      })
    }
  }

  subscribe = (event, callback) => {
    if (this._subscribers[event] === undefined) this._subscribers[event] = []
    this._subscribers[event].push(callback)
    this._bindListeners(event)
    return this.unsubscribe.bind(null, event, callback)
  }

  unsubscribe = (event, callback) => {
    if (this._subscribers[event] === undefined) return
    this._subscribers[event] = this._subscribers[event].filter(s => s !== callback)
    this._bindListeners(event)
  }

  // Exposes NextRouter methods
  push = NextRouter.push
  replace = NextRouter.replace
  back = NextRouter.back
  beforePopState = NextRouter.beforePopState
}

// Router should be treated as a replacement for next's Router
// (i.e. it should be able to do everything next's Router does & more)
export const Router = new _Router()
